import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi";

import radioCheck from "assets/svg/radio-check.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionStampPaperForEvidence,
  saveOppositionStampPaperPaymentToVendor,
  type OppositionStampPaperForEvidenceItem,
} from "api/opposition";

export const OPPOSITION_STAMP_PAPER_FROM_VENDOR_FORM_ID =
  "opposition-stamp-paper-from-vendor-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type LineDraft = {
  amountPayable: string;
  paid: "" | "true" | "false";
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionStampPaperFromVendorView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState<OppositionStampPaperForEvidenceItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [activeIdx, setActiveIdx] = useState(0);
  const [stampPaperRate, setStampPaperRate] = useState(0);
  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        calcTooltipRef.current &&
        !calcTooltipRef.current.contains(e.target as Node)
      ) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionStampPaperForEvidence(leadId)
      .then((data) => {
        if (cancelled) return;
        setStampPaperRate(data.stampPaperRate ?? 0);
        // Only marks with saved Step 8 are valid for Step 9.
        const eligible = data.conflictingMarks.filter(
          (m) => m.stampPapersForEvidence != null && m.stampVendorNameForEvidence
        );
        setMarks(eligible);
        const next: Record<string, LineDraft> = {};
        eligible.forEach((m) => {
          next[m.id] = {
            amountPayable:
              m.amountPayableToStampVendorForEvidence != null
                ? String(m.amountPayableToStampVendorForEvidence)
                : String(m.defaultAmountPayableToStampVendorForEvidence ?? ""),
            paid:
              m.paidToVendorForEvidence === true
                ? "true"
                : m.paidToVendorForEvidence === false
                  ? "false"
                  : "",
          };
        });
        setDrafts(next);
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load stamp paper status"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeMark = marks[activeIdx] ?? null;
  const activeDraft = activeMark ? drafts[activeMark.id] : null;

  const updateActiveDraft = (patch: Partial<LineDraft>) => {
    if (!activeMark) return;
    setDrafts((prev) => ({
      ...prev,
      [activeMark.id]: { ...prev[activeMark.id], ...patch },
    }));
  };

  const validate = (): boolean => {
    if (marks.length === 0) {
      toast.error("Save Step 8 (Stamp Paper details to Vendor) first.");
      return false;
    }
    for (const m of marks) {
      const d = drafts[m.id];
      if (!d || (d.paid !== "true" && d.paid !== "false")) {
        toast.error(
          `Select Paid Yes/No for ${m.applicationNo ?? "application"}.`
        );
        return false;
      }
      if (d.amountPayable.trim() !== "") {
        const v = Number(d.amountPayable);
        if (!Number.isFinite(v) || v < 0) {
          toast.error(
            `Enter a valid Amount Payable for ${m.applicationNo ?? "application"}.`
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!validate()) return;
    setSaving(true);
    try {
      const fresh = await saveOppositionStampPaperPaymentToVendor(leadId, {
        items: marks.map((m) => {
          const d = drafts[m.id]!;
          const amount = d.amountPayable.trim();
          return {
            conflictingMarkId: m.id,
            ...(amount === ""
              ? {}
              : { amountPayableToStampVendorForEvidence: Number(amount) }),
            paidToVendorForEvidence: d.paid === "true",
          };
        }),
      });
      setMarks((prev) =>
        prev.map((m) => fresh.conflictingMarks.find((f) => f.id === m.id) ?? m)
      );
      toast.success("Stamp paper payment to vendor saved.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save payment to vendor"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Save the Stamp Paper details to Vendor (Step 8) for at least one
          application before recording payment to the vendor.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_STAMP_PAPER_FROM_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(m.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      {/* Row: Amount Payable | Paid to Vendor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <label className={LABEL_CLASS}>
              Amount Payable to Stamp Vendor for Evidence
            </label>
            <div className="relative" ref={calcTooltipRef}>
              <button
                type="button"
                onClick={() => setCalcTooltipOpen((o) => !o)}
                onMouseEnter={() => setCalcTooltipOpen(true)}
                className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                aria-label="Show calculation"
              >
                <HiOutlineInformationCircle
                  className="w-4 h-4"
                  strokeWidth={1.5}
                />
              </button>
              {calcTooltipOpen && (
                <div
                  className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p className="text-xs font-semibold text-[#101111] mb-2">
                    Calculation Breakdown
                  </p>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                      <span className="text-[#6D6D6D]">
                        Stamp Papers ({activeMark?.stampPapersForEvidence ?? 0}{" "}
                        × Rs. {stampPaperRate})
                      </span>
                      <span>
                        Rs.{" "}
                        {(
                          activeMark?.defaultAmountPayableToStampVendorForEvidence ??
                          0
                        ).toLocaleString("en-IN")}
                        /-
                      </span>
                    </div>
                    <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                      <span>Auto-calculated</span>
                      <span>
                        Rs.{" "}
                        {(
                          activeMark?.defaultAmountPayableToStampVendorForEvidence ??
                          0
                        ).toLocaleString("en-IN")}
                        /-
                      </span>
                    </div>
                    {activeDraft?.amountPayable.trim() !== "" &&
                      Number(activeDraft?.amountPayable) !==
                        (activeMark?.defaultAmountPayableToStampVendorForEvidence ??
                          0) && (
                        <p className="mt-2 text-[11px] text-[#6D6D6D]">
                          Override in use. Clear the field to fall back to the
                          auto-calculated amount.
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <input
            type="number"
            min={0}
            step="0.01"
            value={activeDraft?.amountPayable ?? ""}
            onChange={(e) => updateActiveDraft({ amountPayable: e.target.value })}
            placeholder="Text Here"
            className={INPUT_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Paid to Vendor</label>
          <Select
            value={activeDraft?.paid ?? ""}
            onValueChange={(v) =>
              updateActiveDraft({ paid: v as "true" | "false" })
            }
          >
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px]">
              <SelectValue placeholder="Text Here" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row: Number of Stamp Papers (readonly, from Step 8) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Number of Stamp Papers needed for Evidence
          </label>
          <input
            type="text"
            value={
              activeMark?.stampPapersForEvidence != null
                ? String(activeMark.stampPapersForEvidence)
                : ""
            }
            placeholder="Set in Stamp Paper details to Vendor (Step 8)"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>
    </form>
  );
}
